import React, { Fragment, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";

import HeaderComponent from "../../components/HeaderComponent";
import TitleH2Component from "../../components/TitleH2Component";
import TitleH4Component from "../../components/TitleH4Component";
import InputComponent from "../../components/InputComponent";
import LoaderComponent from "../../components/LoaderComponent";
import AlertComponent from "../../components/AlertComponent";
import ModalGeneralComponent from "../../components/ModalGeneralComponent";
import MoneyInputComponent from "../../components/MoneyInputComponent";

import stylebtn from "../../styles/btn.module.scss";

import {
  getAgendaId,
  postActividades,
  putActividades,
  deleteActividad,
} from "../../api/Agenda";
import { useFormatter } from "../../hooks/useFormatter";
import { useFechaActual } from "../../hooks/useFechaActual";
import PaginadorComponent from "../../components/PaginadorComponent";
import { useEstadoAgenda } from "../../hooks/useEstadoAgenda";
import { useAuth } from "../../context/AuthContext";

const DetalleNotasPages = () => {
  const { id } = useParams();

  // Estados globales
  const [loading, setLoading] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const [alert, setAlert] = useState(false);

  const { formatDate, formatDateUTC } = useFormatter();
  // Agenda y actividades
  const [agenda, setAgenda] = useState(null);
  const [actividades, setActividades] = useState([]);

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [showEliminar, setShowEliminar] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [actividadId, setActividadId] = useState(null);
  const [tipo, setTipo] = useState("agenda");
  const { calcularDiferenciaDias } = useFechaActual();
  const { isAdmin } = useAuth();
  // Formularios
  const [formDataActividad, setFormDataActividad] = useState({
    monto: "",
    descripcion: "",
    fecha_caducidad: "",
  });

  // Búsqueda
  const [busqueda, setBusqueda] = useState("");

  const { calcularEstado } = useEstadoAgenda();

  // ---------------------------- //
  // Función para mostrar mensajes
  // ---------------------------- //

  const handleChangeBusqueda = (event) => {
    setBusqueda(event.target.value);
  };
  const showMensaje = (msg, duracion = 3000) => {
    setMensaje(msg);
    setAlert(true);
    setTimeout(() => {
      setMensaje("");
      setAlert(false);
    }, duracion);
  };

  // ---------------------------- //
  // Cargar agenda y actividades
  // ---------------------------- //
  const fetchData = async (filters = {}) => {
    if (!id) return;
    setLoading(true);
    try {
      const resAgenda = await getAgendaId(id, filters);

      setAgenda(resAgenda?.data);
      setTipo(resAgenda?.data?.tipo);
      setActividades(resAgenda?.data?.actividades || []);
    } catch (error) {
      console.error("Error cargando datos:", error);
      showMensaje("Error cargando datos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, [id]);

  // ---------------------------- //
  // Búsqueda
  // ---------------------------- //
  const buscarNota = async (e) => {
    e.preventDefault();
    const filters = {
      searchQuery: busqueda,
    };
    fetchData(filters);
  };

  // ---------------------------- //
  // Handlers de formulario
  // ---------------------------- //
  const handleInputChangeActividad = (e) => {
    const { name, value } = e.target;
    setFormDataActividad((prev) => ({ ...prev, [name]: value }));
  };

  const handleShowAdd = () => {
    setShowEdit(false);
    setActividadId(null);
    setFormDataActividad({ monto: "", descripcion: "", fecha_caducidad: "" });
    setTipo(agenda?.tipo || "agenda");
    setShowModal(true);
  };

  const handleShowEdit = (item) => {
    setShowEdit(true);
    setActividadId(item.id);
    setFormDataActividad({
      monto: item.monto || "",
      descripcion: item.descripcion || "",
      fecha_caducidad: item.fecha_caducidad?.split("T")[0] || "",
    });
    setTipo(agenda?.tipo || "agenda");
    setShowModal(true);
  };

  const handleCloseModal = () => setShowModal(false);

  // ---------------------------- //
  // Guardar / Editar actividad
  // ---------------------------- //
  const handleSaveActividad = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        id_agenda: id,
        monto: formDataActividad.monto || null,
        descripcion: formDataActividad.descripcion,
        fecha_caducidad: formDataActividad.fecha_caducidad,
        tipo,
      };

      const res = await postActividades(payload);
      if (res.success) {
    
        showMensaje("Nota registrada con éxito");
        handleCloseModal();
        fetchData();
        setFormDataActividad({
          monto: "",
          descripcion: "",
          fecha_caducidad: "",
        });
      }
    } catch (err) {
      console.error(err);
      showMensaje("Error al guardar la nota");
    }
  };

  const handleEditActividad = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        monto: formDataActividad.monto || null,
        descripcion: formDataActividad.descripcion,
        fecha_caducidad: formDataActividad.fecha_caducidad,
        tipo,
      };

      const res = await putActividades(actividadId, payload);
      if (res.success) {
        showMensaje("Nota editada con éxito");
        handleCloseModal();
        fetchData();
        setFormDataActividad({
          monto: "",
          descripcion: "",
          fecha_caducidad: "",
        });
      }
    } catch (err) {
      console.error(err);
      showMensaje("Error al editar la nota");
    }
  };

  // ---------------------------- //
  // Eliminar registro
  // ---------------------------- //
  const handleShowEliminar = (item) => {
    setActividadId(item?.id);
    setShowEliminar(true);
  };
  const handleCloseEliminar = () => setShowEliminar(false);

  const eliminarRegistro = async () => {
    try {
      const res = await deleteActividad(actividadId);
      if (res.success) {
        showMensaje("Registro eliminado con éxito");
        fetchData();
        handleCloseEliminar();
      }
    } catch (err) {
      console.error(err);
      showMensaje("Error eliminando registro");
    }
  };

  const handleGoBack = () => {
    // Retrocede una página en el historial
    window.history.back();
  };

  // ---------------------------- //
  // Paginación (versión correcta)
  // ---------------------------- //
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 7;

  // Calcula total de páginas según las actividades
  const pageCount = Math.ceil(actividades.length / itemsPerPage);

  // Obtiene los elementos de la página actual
  const currentPageData = actividades.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  useEffect(() => {
    setCurrentPage(0);
  }, [actividades]);

  return (
    <Fragment>
      <div className="d-flex justify-content-center">
        <AlertComponent
          mensaje={mensaje}
          activaalert={alert}
          styleGroup="bg-success color-white"
        />
      </div>

      <HeaderComponent>
        {agenda && (
          <TitleH2Component styleGroup="box-text color-dark fw700 roboto mb-4 uppercase">
            {`DETALLE ${agenda?.tipo}`.toUpperCase()}
          </TitleH2Component>
        )}
        <button
          type="button"
          onClick={handleShowAdd}
          className={`${stylebtn.btn} ${stylebtn.primary}`}
        >
          AGREGAR
        </button>
      </HeaderComponent>

      {loading ? (
        <LoaderComponent styleGroup="mt-5" />
      ) : (
        <div className="conatiner-global pt-3 mb-5">
          <section>
            <div className="container-fluid">
              <div className="d-flex justify-content-end m-3">
                <button
                  type="button"
                  onClick={handleGoBack}
                  className={`${stylebtn.btn_desnudo} ${stylebtn.icon} ms-3 p-0 d-inline-flex color-dark`}
                >
                  <Icon icon="icon-park-solid:back" />
                </button>
              </div>
              <div className="row gy-4 justify-content-between">
                {agenda && (
                  <div className="col-12 col-md-6 col-xl-4">
                    <TitleH2Component styleGroup="box-text color-dark fw700 roboto mb-1 text-center text-md-start">
                      {agenda?.nombre}
                    </TitleH2Component>
                    <TitleH4Component styleGroup="box-text color-dark fw700 roboto mb-1 text-center text-md-start">
                      {agenda?.celular}
                    </TitleH4Component>
                  </div>
                )}
                <div className="col-12 col-md-6 col-xl-5 d-flex justify-content-end">
                  <form
                    onSubmit={buscarNota}
                    className="d-flex gap-2 align-items-end"
                  >
                    <InputComponent
                      placeholder="Buscar nota"
                      label="Buscar Nota"
                      styleLabel="fw600 WorkSans"
                      htmlFor="bloque"
                      type="search"
                      value={busqueda}
                      onChange={handleChangeBusqueda}
                    />
                    <button
                      type="submit"
                      className={`${stylebtn.btn} ${stylebtn.primary}`}
                    >
                      BUSCAR
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </section>

          <section>
            <div className="table-responsive mt-4">
              <table className="table table-bordered tableGeneral">
                <thead>
                  <tr>
                    <th scope="col" className="color-madera">
                      #
                    </th>
                    <th scope="col" className="color-madera w-50">
                      Descripción
                    </th>
                    {tipo === "agenda" && (
                      <th scope="col" className="color-madera">
                        Monto
                      </th>
                    )}
                    <th scope="col" className="color-madera">
                      Registro
                    </th>
                    <th scope="col" className="color-madera">
                      Vencimiento
                    </th>
                    <th scope="col" className="color-madera">
                      Estado
                    </th>
                    {isAdmin && <th scope="col" className="color-madera">
                      Editar
                    </th>}
                   {isAdmin && <th scope="col" className="color-madera">
                      Eliminar
                    </th>}
                  </tr>
                </thead>
                <tbody>
                  {currentPageData.length > 0 ? (
                    currentPageData.map((item, index) => {
                      const { estado, bg } = calcularEstado(
                        item.fecha_caducidad
                      );
                      return (
                        <tr key={item?.id}>
                          <th>{index + 1}</th>
                          <td>{item?.descripcion}</td>
                          {tipo === "agenda" && (
                            <td>
                              {new Intl.NumberFormat("es-CO", {
                                style: "currency",
                                currency: "COP",
                                minimumFractionDigits: 0,
                              }).format(item?.monto)}
                            </td>
                          )}
                          <td>{formatDate(item?.createdAt)}</td>
                          <td>{formatDateUTC(item?.fecha_caducidad)}</td>
                          <td className={`${bg} text-nowrap`}>{estado}</td>
                          {isAdmin && <td className="text-center">
                            <Link
                              onClick={() => handleShowEdit(item)}
                              className={`${stylebtn.btn_desnudo} ${stylebtn.icon} p-0`}
                            >
                              <Icon
                                icon="raphael:edit"
                                // width="30"
                                // height="30"
                              />
                            </Link>
                          </td>}
                          {isAdmin && <td className="text-center">
                            <button
                              onClick={() => handleShowEliminar(item)}
                              className={`${stylebtn.btn_desnudo} ${stylebtn.icon} p-0 color-danger`}
                            >
                              <Icon
                                icon="majesticons:delete-bin"
                                // width="30"
                                // height="30"
                              />
                            </button>
                          </td>}
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center">
                        No hay actividades registradas
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
          {pageCount > 1 && (
            <PaginadorComponent
              setCurrentPage={setCurrentPage}
              pages={pageCount}
              styleGroup="d-flex justify-content-center pt-4"
            />
          )}
        </div>
      )}

      {/* Modal Crear / Editar */}
      <section>
        <ModalGeneralComponent
          ModalHeader
          titulo={`${showEdit ? "EDITAR" : "AGREGAR"} NOTA`}
          styleGroup="d-flex align-items-center modal-general"
          styleTitle="box-text color-madera fw700 roboto text-center WorkSans"
          show={showModal}
          handleClose={handleCloseModal}
        >
          <div className="container">
            <form
              onSubmit={showEdit ? handleEditActividad : handleSaveActividad}
            >
              {(tipo === "recordatorio" || tipo === "agenda") && (
                <>
                  {tipo === "agenda" && (
                    <MoneyInputComponent
                      label="Monto"
                      placeholder="Monto"
                      styleLabel="fw600 WorkSans"
                      htmlFor="monto"
                      name="monto"
                      value={formDataActividad.monto}
                      onChange={handleInputChangeActividad}
                    />
                  )}
                  <textarea
                    name="descripcion"
                    placeholder="Descripción"
                    className="form-control mt-3"
                    rows={3}
                    value={formDataActividad.descripcion}
                    onChange={handleInputChangeActividad}
                  ></textarea>
                  <InputComponent
                    label="Fecha vencimiento"
                    placeholder="Fecha"
                    styleLabel="fw600 WorkSans"
                    htmlFor="fecha"
                    type="date"
                    name="fecha_caducidad"
                    value={formDataActividad.fecha_caducidad}
                    onChange={handleInputChangeActividad}
                  />
                </>
              )}
              <div className="d-flex justify-content-end mt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className={`btn ${stylebtn.btn} ${stylebtn.danger}`}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className={`btn ${stylebtn.btn} ${stylebtn.primary} ms-3`}
                >
                  Guardar
                </button>
              </div>
            </form>
          </div>
        </ModalGeneralComponent>
      </section>

      {/* Modal eliminar */}
      <section>
        <ModalGeneralComponent
          styleGroup="d-flex align-items-center modal-general"
          styleTitle="box-text color-madera fw700 roboto text-center WorkSans"
          show={showEliminar}
          handleClose={handleCloseEliminar}
          handleShow={handleShowEliminar}
        >
          <form>
            <TitleH2Component styleGroup="box-text color-madera fw700 roboto mb-4 text-center">
              ¿Seguro que quieres eliminar el registro?
            </TitleH2Component>
            <div className="d-flex justify-content-end">
              <button
                type="button"
                onClick={handleCloseEliminar}
                className={`btn ${stylebtn.btn} ${stylebtn.danger}`}
              >
                Cancelar
              </button>
              <button
                onClick={eliminarRegistro}
                type="button"
                className={`btn ${stylebtn.btn} ${stylebtn.primary} ms-3`}
              >
                SI
              </button>
            </div>
          </form>
        </ModalGeneralComponent>
      </section>
    </Fragment>
  );
};

export default DetalleNotasPages;
